<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useClaimStore, useContextStore, useNotificationStore } from "@/store";
import { computePermissionForCurrentUser } from '@/utils/directives/v-permission';

import ConversationClaimReasonListbox from "./ClaimReasonListbox.vue";
import ClaimRequests from './ClaimRequests.vue';
import { ReceiptRefundIcon } from "@heroicons/vue/20/solid";

import { ExpenseClaimDocument, File, RetrieveClaimWithRelationsResponse } from "@/interfaces/data-contracts";
import TextEditor from "../ui/TextEditor.vue";
import { Flight } from '@/interfaces';
import SetTravelAgencyAgent from './SetTravelAgencyAgent.vue';
import SetTravelAgencyCompany from '../overview/SetBusinessTravelCompany.vue';
import { BuildingOffice2Icon, ChatBubbleOvalLeftEllipsisIcon, ChatBubbleLeftRightIcon, FolderOpenIcon, LockClosedIcon, UserIcon, MapIcon, ScaleIcon, QuestionMarkCircleIcon } from '@heroicons/vue/24/solid';
import { copyToClipboard } from '@/utils';
import Expenses from '../expenses/Expenses.vue';
import ClaimDocuments from './ClaimDocuments.vue';
import adminAPI from '@/services/api';
import AdditionalRequests from '../additional-requests/AdditionalRequests.vue';


const props = defineProps<{
  claim: RetrieveClaimWithRelationsResponse;
}>();
const emits = defineEmits(["update"]);

const claim = computed(() => props.claim);

const adminNoteData = ref<Object>({});
const customerNoteData = computed(() => claim.value.customerNotes);
const passengersQuestion = computed(() => claim.value.passengersQuestion);

async function saveAdminNote() {
  const { success } = await adminAPI.saveAdminNote(props.claim.id, adminNoteData.value);
  if (success) useNotificationStore().emitNotification({ title: "Note enregistrée avec succès"});
}

async function saveCustomerNote() {
  const { success } = await adminAPI.saveCustomerNote(props.claim.id, {
    customerNotes: customerNoteData.value,
    passengersQuestion: passengersQuestion.value,
  });
  if (success) useNotificationStore().emitNotification({ title: "Note client enregistrée avec succès"});
}

async function onUpdateNote(editorJsData) {
  adminNoteData.value = editorJsData
}
</script>

<template>
<div class="px-2 py-3 bg-white">
  <div class="mb-4 overflow-hidden text-left bg-white border rounded shadow-md sm:rounded-lg">
    <div class="py-5 sm:p-0">
      <div class="p-6 shadow bg-gray-50">
        <h3 class="inline-flex text-base font-semibold leading-7 text-gray-900">
          Résumé du dossier
          <span class="inline-flex text-slate-800 cursor-pointer items-center px-2 py-0.5 ml-2 text-xs font-medium rounded-md bg-gray-50 ring-1 ring-inset ring-gray-500/20" @click="copyToClipboard(useClaimStore().claim?.claimNumber.toString())" v-tooltip="'Notre référence'">
            № #{{ useClaimStore().claim?.claimNumber }}
          </span>
          <span @click="copyToClipboard(useClaimStore().claim.legalSpecification.impactedSegment.reservation.gdsPnrId)" class="inline-flex cursor-pointer items-center px-2 py-0.5 ml-2 text-xs font-medium text-gray-600 rounded-md bg-gray-50 ring-1 ring-inset ring-gray-500/20">
            PNR: {{ useClaimStore().claim.legalSpecification.impactedSegment.reservation.gdsPnrId }}
          </span>
        </h3>
        <p class="max-w-2xl mt-1 text-sm leading-6 text-gray-500">Aperçu de la demande, des pièces et des personnes impactées.</p>
      </div>

      <div class="px-4">
        <dl class="sm:divide-y sm:divide-gray-200">
          <div v-permission="['UPCLAIM_ADMIN', 'LAWYER']" class="py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm font-medium leading-6 text-gray-700">
              <span class="inline-flex">
                <MapIcon class="w-5 h-5 mr-2 text-gray-400" />
                Agence
              </span>
            </dt>
            <dd class="mt-1 text-sm text-gray-700 sm:mt-0 sm:col-span-2">
              <img class="rounded max-w-40" :src="claim.tenantAssociation.sourceTenant.tenantBranding?.logoUrl" />
            </dd>
          </div>

          <ClaimRequests :claim="claim" />

          <div class="py-4 bg-white sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm font-medium leading-6 text-gray-700">
              <span class="inline-flex">
                <UserIcon class="w-5 h-5 mr-2 text-gray-400" />
                Agent
              </span>
            </dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <SetTravelAgencyAgent :claim-id="claim.id" :current-admin="claim?.legalSpecification.impactedSegment?.reservation.travelAgencyAgent?.admin" class="w-full" />
            </dd>
          </div>

          <div class="py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm font-medium leading-6 text-gray-700">
              <span class="inline-flex">
                <BuildingOffice2Icon class="w-5 h-5 mr-2 text-gray-400" />
                Entreprise
              </span>
            </dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <SetTravelAgencyCompany
                :claim-id="claim.id"
                :current-travel-agency-company="claim?.legalSpecification.impactedSegment?.reservation.travelAgencyCompany"
              />
            </dd>
          </div>

          <div class="py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm font-medium leading-6 text-gray-700">
              <span class="inline-flex">
                <ChatBubbleLeftRightIcon class="flex-shrink-0 w-5 h-5 mr-2 text-gray-400" />
                Raison invoquée
              </span>
            </dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <ConversationClaimReasonListbox class="w-full" />
            </dd>
          </div>
          
          <div v-permission="['UPCLAIM_ADMIN', 'LAWYER']" class="py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="inline-flex text-sm font-medium leading-6 text-gray-700">
              <span class="inline-flex">
                <LockClosedIcon class="w-5 h-5 mr-2 text-gray-400" />
                Notes internes
              </span>
            </dt>
            <dd class="flex flex-col items-end mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <TextEditor
                  class="mb-5"
                  :initial-data="(claim.adminNotes as any)"
                  @update-note="onUpdateNote"
                  :can-edit="computePermissionForCurrentUser(['UPCLAIM_ADMIN', 'LAWYER'])"
              />
            <button
                  v-permission="['UPCLAIM_ADMIN', 'LAWYER']"
                  type="button"
                  @click="saveAdminNote"
                  class="inline-flex items-center px-3 py-2 text-sm font-semibold text-center text-gray-900 bg-white rounded-md shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
              Sauvegarder
            </button>
            </dd>
          </div>

          <div class="py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm font-medium leading-6 text-gray-700">
              <span class="inline-flex">
                <ChatBubbleOvalLeftEllipsisIcon class="flex-shrink-0 w-5 h-5 mr-2 text-gray-400" />
                Notes agents
              </span>
            </dt>
            <dd class="flex flex-col items-end mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <textarea :readonly="false" v-model="useClaimStore().claim.customerNotes" :rows="4" name="agent-notes" id="agent-notes" class="mb-5 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6" />
              <button
                  v-permission="['UPCLAIM_ADMIN', 'LAWYER']"
                  type="button"
                  @click="saveCustomerNote"
                  class="inline-flex items-center px-3 py-2 text-sm font-semibold text-center text-gray-900 bg-white rounded-md shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                Sauvegarder
              </button>
            </dd>
          </div>

          <div class="py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm font-medium leading-6 text-gray-700">
              <span class="inline-flex">
                <QuestionMarkCircleIcon class="flex-shrink-0 w-5 h-5 mr-2 text-gray-400" />
                Question Passagers
              </span>
            </dt>
            <dd class="flex flex-col items-end mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <textarea :readonly="false" v-model="useClaimStore().claim.passengersQuestion" :rows="4" name="passengers-question" id="passengers-question" class="mb-5 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6" />
              <button
                  v-permission="['UPCLAIM_ADMIN']"
                  type="button"
                  @click="saveCustomerNote"
                  class="inline-flex items-center px-3 py-2 text-sm font-semibold text-center text-gray-900 bg-white rounded-md shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                Sauvegarder
              </button>
            </dd>
          </div>
          <div class="py-6 sm:px-6">
            <div class="sm:grid sm:grid-cols-3 sm:gap-4">
              <dt class="text-sm font-medium leading-6 text-gray-700">
                <span class="inline-flex">
                  <FolderOpenIcon class="w-5 h-5 mr-2 text-gray-400" />
                  Documents
                </span>
              </dt>
            
              <dd class="col-span-2">
              </dd>
            </div>
            <ClaimDocuments :claim="claim" />
          </div>

          <div class="py-6 -mx-4">
            <Expenses :claim-expenses="claim.claimExpenses" @update="emits('update')">
              <template v-slot:header>
                <div class="px-3 text-left">
                  <dt class="text-sm font-medium leading-6 text-gray-700">
                    <span class="inline-flex">
                      <ReceiptRefundIcon class="w-5 h-5 mr-2 text-gray-400" />
                      Préjudices supplémentaires
                    </span>
                  </dt>
                </div>
              </template>
            </Expenses>
          </div>
          

          <div class="py-6 -mx-4" v-permission="['LAWYER', 'UPCLAIM_ADMIN']">
            <AdditionalRequests :additionalRequests="claim.additionalRequests" @update="emits('update')">
              <template v-slot:header>
                <div class="px-3 text-left">
                  <dt class="text-sm font-medium leading-6 text-gray-700">
                    <span class="inline-flex">
                      <ScaleIcon class="w-5 h-5 mr-2 text-gray-400" />
                      Demandes supplémentaires
                    </span>
                    <p class="my-1 text-xs text-gray-500">
                      Hors Convention de Montréal (Ex: « Résistance abusive »)
                    </p>
                  </dt>
                </div>
              </template>
            </AdditionalRequests>
          </div>

        </dl>
      </div>
    </div>
  </div>
</div>
</template>
